import type { View } from "@novorender/api";
import type {
    BoundingSphere,
    HierarcicalObjectReference,
    ObjectData,
    ObjectId,
    SearchPattern,
} from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";
import pMap from "p-map";

import { type AppObjectDB } from "utils/appObjectDb";
import { mergeBoundingSpheres, unionAABB3 } from "utils/math";
import { getNodeClass, getParentPath, getParentPaths, isLeafNode, NodeClass } from "utils/objectData";
import { batchedPropertySearch, getDescendants, preloadDescendantsFastPath } from "utils/search";

import {
    highlightIdsFor,
    resolveSelectionLegacy,
    selectionTargetFor,
    singleSelectionTarget,
} from "./selectionResolution";
import {
    type BoundsOptions,
    type FindIdsOptions,
    type ObjectDataAccess,
    type PropertyQuery,
    type PropertyTerm,
    type SelectionBounds,
    type SelectionResolutionMode,
    type SelectionResolutionOptions,
    type SelectionTarget,
} from "./types";
import { getObjectsByIdsOffline, objectBoundsAsSelection } from "./utils";

/**
 * Cap on raw records an ancestor walk will read — a safety net against a
 * pathological stream, not a bound on the chain itself (which is known from the
 * path). Aligned with the model tree's own scan budget.
 */
const MAX_ANCESTOR_SCAN = 5000;

/**
 * A query term as a `SearchPattern`. `exact` is what makes an equality match rather
 * than the server's default substring one; `range` and a bare `property` (the
 * "present at all" probe) carry no value of their own.
 */
function toSearchPattern(term: PropertyTerm): SearchPattern {
    const pattern: SearchPattern = { property: term.property, exclude: term.negate };
    if (term.anyOf) {
        return { ...pattern, value: [...term.anyOf], exact: true };
    }
    if (term.range) {
        return { ...pattern, range: term.range };
    }
    return { ...pattern, value: "" };
}

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * The default data access: the scene's own metadata when the view carries it,
 * otherwise the object DB over the network.
 *
 * That either/or already existed inside `utils/search.getObjectData`; this gives
 * it a name and a place, so callers stop passing `db` and `view` around.
 */
export class ObjectDbDataAccess implements ObjectDataAccess {
    readonly kind = "object-db" as const;

    constructor(
        private readonly db: AppObjectDB,
        private readonly view: View,
    ) {}

    isIndexAvailableOffline() {
        return false;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * The scene's own metadata when the view carries it, otherwise the object DB. A
     * `MetaDataEntry` is an `ObjectData` without its `loadMetaData`/`save` methods,
     * which no consumer of this call uses — hence the cast on that branch.
     *
     * Neither read takes a signal, so an aborted call is answered before it starts rather
     * than cancelled in flight — enough to keep a superseded pick from waiting out a
     * request whose answer is already unwanted.
     */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        if (signal?.aborted) {
            return Promise.resolve(undefined);
        }
        return this.view.data
            ? (this.view.data.getObjectMetaData(id) as unknown as Promise<ObjectData>)
            : this.db.getObjectMetdata(id).catch(() => undefined);
    }

    getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        return this.firstObjectAtPath(path, signal);
    }

    /**
     * By path — the only identity `db.search` accepts — using the path the object
     * already carries, so this is one round trip with no id resolution.
     */
    getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const parentPath = getParentPath(object.path);
        if (!parentPath) {
            return Promise.resolve(undefined);
        }
        return this.firstObjectAtPath(parentPath, signal);
    }

    /**
     * Straight through: `db.search` is already a lazily-paging iterator of exactly
     * this type, keyed by the path the object carries (the root listing is the empty
     * path). No `full: true` — a listing needs identity and node type, not every
     * property of every row; each reference carries `loadMetaData()` for the row that
     * is opened.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        return this.db.search({ parentPath: parent?.path ?? "", descentDepth: 1 }, signal);
    }

    /**
     * One `SearchPattern` per term — the shape `db.search` already takes, since
     * `SearchPattern[]` is ANDed and a `value` array is its own any-of.
     *
     * An empty term list would search for everything rather than nothing, so it
     * answers empty here instead of asking.
     */
    findByProperties(
        { terms }: PropertyQuery,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        if (!terms.length) {
            return emptyIterator();
        }
        return this.db.search({ searchPattern: terms.map(toSearchPattern) }, signal);
    }

    /**
     * No bulk expansion here, so the subtrees are one request per non-leaf match; the
     * fan-out overlaps them with the paging of the matches instead of draining the
     * iterator first. Leaves cost nothing — `db.search` rows carry a real node type.
     */
    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants, signal } = options ?? {};

        // A set, not the pieces: subtrees overlap heavily where one match sits under
        // another, so their sum can be many times the distinct answer.
        const ids = new Set<number>();

        await pMap(
            this.findByProperties(query, signal),
            async (object) => {
                ids.add(object.id);
                if (!includeDescendants || isLeafNode(object)) {
                    return;
                }
                try {
                    for (const id of await this.getDescendantIds(object, signal)) {
                        ids.add(id);
                    }
                } catch (e) {
                    // One unreadable subtree costs its own ids, not the whole answer, as
                    // in `objectActions.fetchAllDescendantIds`.
                    if (!signal?.aborted) {
                        console.error(`[object-db] could not read the subtree of object ${object.id}:`, e);
                    }
                }
            },
            // No `signal` option: it makes pMap reject with an AbortError, where this
            // interface answers an abort with an empty value. Same reason
            // `objectActions.fetchAllDescendantIds` omits it.
            { concurrency: 6 },
        );

        return signal?.aborted ? new Uint32Array() : new Uint32Array(ids);
    }

    /** A non-exact `name` pattern, which is what the server treats as "contains". */
    searchByName(term: string, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference> {
        return this.db.search({ searchPattern: [{ property: "name", value: term }] }, signal);
    }

    /**
     * A negative `descentDepth` searches upwards, but it answers with a flat stream
     * rather than a chain: duplicate-path records (common in IFC/RVT exports) repeat
     * the same ancestors, so the walk keeps the first record per expected path — the
     * chain itself is known upfront from the path string — and caps how many raw
     * records it will scan. Ancestors the search never returns (permission filtering,
     * the cap) are left out.
     *
     * `full: true`, so each record arrives carrying its properties: the chain is walked to
     * be classified, and a listing without them costs a metadata read per ancestor on top
     * of the one search. Chain is not long, so there's little cost in the extra metadata.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        // Root first, and never the synthesized root itself.
        const expected = [...getParentPaths(object.path)].filter((path) => path !== "");
        if (!expected.length) {
            return [];
        }

        const missing = new Set(expected);
        const byPath = new Map<string, HierarcicalObjectReference>();
        const iterator = this.db.search(
            { descentDepth: -(expected.length + 1), parentPath: object.path, full: true },
            signal,
        );

        // Driven by hand rather than `for await`, which means the generator has to be
        // closed by hand too: every exit below leaves it suspended mid-page otherwise,
        // holding its paging state for as long as the iterator is reachable.
        try {
            for (let scanned = 0; scanned < MAX_ANCESTOR_SCAN && missing.size > 0 && !signal?.aborted; scanned++) {
                const next = await iterator.next();
                if (next.done) {
                    break;
                }
                if (missing.delete(next.value.path)) {
                    byPath.set(next.value.path, next.value);
                }
            }
        } finally {
            await iterator.return?.();
        }

        return expected.map((path) => byPath.get(path)).filter((found) => found !== undefined);
    }

    /**
     * The pre-computed file only — never the subtree walk.
     */
    warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean> {
        return preloadDescendantsFastPath({ db: this.db, parentNode: object, abortSignal: signal });
    }

    /**
     * The pre-computed descendants file when the scene has one, otherwise a walk of the
     * subtree. `getDescendants` handles the file, the per-scene cache and the
     * de-duplication of concurrent calls, and throws when there is no file.
     */
    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        try {
            const ids = await getDescendants({ db: this.db, parentNode: object, abortSignal: signal });
            // `getDescendants` is declared `ObjectId[]` but resolves the packed array it
            // caches; a reference's own `descendants` field really is a plain array.
            return ids instanceof Uint32Array ? ids : new Uint32Array(ids);
        } catch {
            const ids: ObjectId[] = [];
            // `descentDepth: undefined` is the whole subtree.
            for await (const reference of this.db.search({ parentPath: object.path }, signal)) {
                ids.push(reference.id);
            }
            return new Uint32Array(ids);
        }
    }

    /**
     * A row per object, folded here — the object DB has no bulk bounds primitive, so
     * this is the one backend that really does read every object. The rows come from
     * the batched id search rather than a metadata read apiece precisely because they
     * carry `bounds`.
     *
     * An object with no bounds contributes nothing rather than blocking the fold, which
     * is what the interface promises and what the two index backends do natively.
     */
    async getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const idArr = Array.isArray(ids) ? ids : Array.from(ids);
        if (!idArr.length) {
            return undefined;
        }

        if (idArr.length === 1) {
            const obj = await this.getObject(idArr[0]);
            return objectBoundsAsSelection(obj?.bounds, parts);
        }

        const rows = await this.getObjectsByIds(idArr, signal ?? new AbortController().signal);
        if (signal?.aborted) {
            return undefined;
        }

        const spheres: BoundingSphere[] = [];
        const boxes: { min: ReadonlyVec3; max: ReadonlyVec3 }[] = [];
        for (const row of rows) {
            if (parts !== "box" && row.bounds?.sphere) {
                spheres.push(row.bounds.sphere);
            }
            if (parts !== "sphere" && row.bounds?.box) {
                boxes.push(row.bounds.box);
            }
        }

        const bounds: SelectionBounds = {};
        const sphere = mergeBoundingSpheres(spheres);
        if (sphere) {
            bounds.sphere = sphere;
        }
        const box = unionAABB3(boxes);
        if (box) {
            bounds.box = box;
        }
        return bounds.sphere || bounds.box ? bounds : undefined;
    }

    /** No `climb` here, so the chain is walked and classified by {@link objectAncestors}. */
    async resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined> {
        const { mode, classAware, signal } = options;

        // Answered before anything is read, which is the whole point of it.
        if (!classAware && mode === "last-object") {
            return singleSelectionTarget(id);
        }

        const clicked = await this.getObject(id, signal);
        if (!clicked || signal?.aborted) {
            return undefined;
        }

        return classAware
            ? this.resolveByNodeClass(clicked, mode, signal)
            : resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    private async resolveByNodeClass(
        clicked: ObjectData,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionTarget | undefined> {
        const clickedIsObject = getNodeClass(clicked.properties) === NodeClass.Object;

        // A click lands on the deepest node of its own chain, so a picked object already
        // *is* the deepest object.
        if (clickedIsObject && mode === "last-object") {
            return selectionTargetFor(this, clicked, "resolved", signal);
        }

        // Only nesting can move `first-object` off a picked object, so its subtree is read
        // speculatively alongside the chain: the common non-nested click then costs what
        // `last-object` does instead of a second round trip.
        const [objects, clickedHighlight] = await Promise.all([
            this.objectAncestors(clicked, signal),
            clickedIsObject ? highlightIdsFor(this, clicked, signal) : undefined,
        ]);

        // Root first, so the shallowest object leads and the deepest ends it.
        const target = mode === "first-object" ? objects[0] : objects.at(-1);
        if (target) {
            return selectionTargetFor(this, target, "resolved", signal);
        }

        if (clickedHighlight) {
            return {
                mainObjectId: clicked.id,
                mainObject: clicked,
                highlightIds: clickedHighlight,
                outcome: "resolved",
            };
        }

        // Nothing above a picked node that is not one itself: a file of only containers and
        // fragments, or a scene parsed before node classes. Legacy rather than the picked
        // node alone, so class-aware never resolves *less* than pre-`NodeClass` would have.
        return resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /**
     * The ancestors that are objects, root first.
     *
     * {@link getAncestors} asks for `full` records, so each row already carries what says
     * its class and `loadMetaData` resolves to the row itself — the whole chain costs the
     * one upward search. A row that somehow arrives without its metadata is dropped rather
     * than failing the click, as reading it through `getObject` used to be.
     *
     * `ObjectData` rather than the bare references, so the resolved ancestor reaches
     * {@link selectionTargetFor} as a metadata read and fills `mainObject` for the panels
     * that open on it.
     */
    private async objectAncestors(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData[]> {
        const ancestors = await this.getAncestors(object, signal);
        const classified = await Promise.all(
            ancestors.map((ancestor) => ancestor.loadMetaData().catch(() => undefined)),
        );
        return classified.filter(
            (ancestor): ancestor is ObjectData => getNodeClass(ancestor?.properties) === NodeClass.Object,
        );
    }

    private async getObjectsByIds(idArr: ObjectId[], abortSignal: AbortSignal) {
        const { data } = this.view;
        if (data) {
            return getObjectsByIdsOffline(this.view, idArr, abortSignal);
        }

        return batchedPropertySearch({
            db: this.db,
            property: "id",
            value: idArr.map(String),
            abortSignal,
        });
    }

    /**
     * The node at `path` itself — `descentDepth: 0` — with its metadata.
     *
     * Only the first record is wanted, so the iterator is closed rather than left
     * suspended: `next()` alone never runs the generator's `return`, unlike the `break`
     * of a `for await`.
     */
    private async firstObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const iterator = this.db.search({ parentPath: path, descentDepth: 0, full: true }, signal);
        try {
            const first = await iterator.next();
            return first.value ? await first.value.loadMetaData() : undefined;
        } catch {
            return undefined;
        } finally {
            await iterator.return?.();
        }
    }
}
